// src/pages/InstallmentScreen.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";

import GatewaySelectionDialog from "../Common/PaymentGatewaySelectionDialog";
import {
  createOnlineOrderRequest,
  resetOnlineOrderState,
} from "../../redux/OnlineOrder/onlineOrderSlice";
import { createOfflineOrderRequest } from "../../redux/OfflineOrder/offlineOrderSlice";
import {
  getPaymentPlatform,
  normalizeGateway,
  savePaymentMeta,
  startPaymentFromOrderResponse,
} from "../Common/PaymentGatewayMethods";
import {
  getAppConfigRequest,
  getBullionRatesRequest,
} from "../../redux/Dashboard/dashboardSlice";
import ReadMore from "react-read-more-read-less";
import { gstHelper } from "../../Utils/GstCalculationResult";
import { ThemeContext } from "@emotion/react";
import { useAppTheme } from "../../theme/useAppTheme";

const InstallmentScreen = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { appConfig, bullionRates } = useSelector((s) => s.dashboard || {});
  const offlineOrder = useSelector((s) => s.offline_order);
  const onlineOrder = useSelector((state) => state.onlineOrder);

  const sip = state?.sipData || {};
  const isMoneySip = !(
    (sip?.bullion && sip?.bullion?._id) ||
    (sip?.bullion && sip?.bullion?.id)
  );

  console.log("sip data", sip);
  const sipPlan = sip?.sipplan || sip?.moneysipplan || {};
  const sipType =
    (sip?.bullion && sip?.bullion?._id) || (sip?.bullion && sip?.bullion?.id)
      ? "Metal SIP"
      : "Money SIP";

  const paymentGateways = appConfig?.paymentGateways || [];
  const decimalPlaces = Number(
    appConfig?.decimalValue ?? appConfig?.decimalPrecision ?? 2,
  );

  const taxType = appConfig?.taxtype || "GST";
  const sipGstEnabled = appConfig?.isSipInstallmentGstEnabled !== false;
  const effectiveTaxType = sipGstEnabled ? taxType : "NONE";
  const jewellerState = appConfig?.jewellerState || "";

  const { customer: fetchedCustomer } = useSelector(
    (state) => state.profile || {},
  );
  const customer = fetchedCustomer || {};
  const customerState = customer?.address?.[0]?.state || "";

  const [tag, setTag] = useState(sip?.tag || "");
  const [agreed, setAgreed] = useState(false);
  const [autoDebit, setAutoDebit] = useState(!!sipPlan?.autodebit);

  const [calculatedGrams, setCalculatedGrams] = useState(0);
  const [ratePerGram, setRatePerGram] = useState(0);
  const [metalCost, setMetalCost] = useState(0);
  const [cgst, setCgst] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [totalPayable, setTotalPayable] = useState(0);
  const [igst, setIgst] = useState(0);
  const [cess, setCess] = useState(0);

  const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [selectedInstallmentForPay, setSelectedInstallmentForPay] =
    useState(null);
  const [loading, setLoading] = useState(false);

  const themeColors = useAppTheme();

  useEffect(() => {
    dispatch(getAppConfigRequest());
    dispatch(
      getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
  }, [dispatch]);

  const calculateReductionValue = (decimalPlaces) =>
    1 / Math.pow(10, decimalPlaces || 0);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(Number(timestamp));
    return date.toLocaleString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const calculateEndDate = (startDate, months) => {
    if (!startDate || !months) return "N/A";
    const end = new Date(Number(startDate));
    end.setMonth(end.getMonth() + Number(months));
    return end.toLocaleString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || Number.isNaN(Number(value)))
      return "₹0.00";
    return Number(value).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatWeight = (value) => {
    if (value === undefined || value === null || Number.isNaN(Number(value)))
      return "0 g";
    return `${Number(value).toFixed(decimalPlaces)} g`;
  };
  useEffect(() => {
    dispatch(resetOnlineOrderState());
  }, []);

  const {
    paidInstallments,
    unpaidInstallments,
    paidCount,
    unpaidCount,
    gramsAccumulated,
  } = useMemo(() => {
    const installments = Array.isArray(sip?.sipInstallments)
      ? sip.sipInstallments
      : [];
    const paid = installments.filter((i) => i.status === "PAID");
    const unpaid = installments.filter(
      (i) => i.status === "PENDING" || i.status === "UNPAID",
    );
    const paidCnt = paid.length;
    const unpaidCnt = Math.max(0, (sip?.noOfMonths || 0) - paidCnt);
    const gramsSum = paid.reduce((s, it) => s + (Number(it.weight) || 0), 0);
    return {
      paidInstallments: paid,
      unpaidInstallments: unpaid,
      paidCount: paidCnt,
      unpaidCount: unpaidCnt,
      gramsAccumulated: gramsSum,
    };
  }, [sip]);

  const percentage = useMemo(() => {
    const planType = (sipPlan?.type || "").toUpperCase();
    switch (planType) {
      case "EVERY_INSTALLMENT_DISCOUNT":
        return Number(sipPlan?.percentage || 0);
      case "FIRST_AND_SUBSEQUENT":
        return Number(
          sipPlan?.subsequent_percentage || sipPlan?.subsequentPercentage || 0,
        );
      case "PREPAID_FIRST_INSTALLMENT":
        return Number(sipPlan?.percentage || 0);
      default:
        return 0;
    }
  }, [sipPlan]);

  useEffect(() => {
    const metalName = sip?.bullion?.metalName || sip?.metalName || "";
    const purity = sip?.bullion?.purity ?? sip?.purity ?? null;
    if (!bullionRates?.length || !appConfig) {
      setCalculatedGrams(0);
      setRatePerGram(0);
      setMetalCost(0);
      setCgst(0);
      setSgst(0);
      setTotalPayable(Number(sip?.installmentAmount || 0));
      return;
    }

    const matched = bullionRates.find((r) => {
      const rn = (r?.bullion?.metalName || "").toString().toLowerCase();
      const sipMetal = (metalName || "").toString().toLowerCase();
      if (r?.rateType !== "Buy") return false;
      if (rn !== sipMetal) return false;
      if (purity != null && r?.bullion?.purity != null) {
        return Number(r.bullion.purity) === Number(purity);
      }
      return true;
    });

    if (!matched) {
      setCalculatedGrams(0);
      setRatePerGram(0);
      setMetalCost(0);
      setCgst(0);
      setSgst(0);
      setTotalPayable(Number(sip?.installmentAmount || 0));
      return;
    }

    const rateWithoutGst = Number(matched.ratePerGram || 0);
    const storedRateWithGst = Number(matched.rateWithGst || 0);
    const rateForGrams = sipGstEnabled ? storedRateWithGst : rateWithoutGst;

    const cgstPercent = sip?.bullion?.cgst ?? appConfig?.gst?.cgst ?? 0;
    const sgstPercent = sip?.bullion?.sgst ?? appConfig?.gst?.sgst ?? 0;
    const igstPercent = sip?.bullion?.tax ?? appConfig?.gst?.igst ?? 0;
    const cessPercent = sip?.bullion?.cess ?? 0;

    const installmentAmount = Number(
      sip?.installmentAmount || sip?.installmentAmt || 0,
    );

    if (!rateForGrams) {
      setCalculatedGrams(0);
      setRatePerGram(rateWithoutGst);
      setMetalCost(0);
      setCgst(0);
      setSgst(0);
      setIgst(0);
      setCess(0);
      setTotalPayable(installmentAmount);
      return;
    }

    let grams = Number(
      (installmentAmount / rateForGrams).toFixed(decimalPlaces),
    );
    const reductionValue = calculateReductionValue(decimalPlaces);

    if (grams * rateForGrams > installmentAmount) {
      grams -= reductionValue;
    }

    const gstAmountResult = gstHelper({
      finalWeight: grams,
      rate: rateWithoutGst,
      igst: igstPercent,
      cgst: cgstPercent,
      sgst: sgstPercent,
      taxRate: igstPercent,
      cess: cessPercent,
      taxType: effectiveTaxType,
      customerState,
      jewellerState,
    });
    setCalculatedGrams(grams);
    setRatePerGram(rateWithoutGst);

    setMetalCost(gstAmountResult.metalCost);
    setCgst(gstAmountResult.cgst);
    setSgst(gstAmountResult.sgst);

    setIgst(
      effectiveTaxType === "GST" &&
        customerState.trim().toLowerCase() !==
          jewellerState.trim().toLowerCase()
        ? gstAmountResult.tax
        : 0,
    );

    setCess(
      effectiveTaxType === "VAT"
        ? Number((gstAmountResult.tax || 0).toFixed(2))
        : 0,
    );

    setTotalPayable(gstAmountResult.finalAmount);
  }, [
    bullionRates,
    appConfig,
    sip,
    decimalPlaces,
    sipGstEnabled,
    effectiveTaxType,
    customerState,
    jewellerState,
  ]);

  const handlePayOnline = (installment = null) => {
    if (!paymentGateways.length) {
      toast.warn("No payment gateways configured!");
      return;
    }
    setSelectedInstallmentForPay(installment);
    if (paymentGateways.length === 1) {
      handleGatewaySelect(paymentGateways[0], installment);
      return;
    }
    setOpenGatewayDialog(true);
  };

  useEffect(() => {
    if (onlineOrder.error) {
      toast.error(
        onlineOrder.error?.message ||
          onlineOrder.error ||
          "Order creation failed",
      );
      setLoading(false);
    }
  }, [onlineOrder.error]);

  useEffect(() => {
    if (!onlineOrder.data || !selectedGateway) return;

    const pay = async () => {
      setLoading(true);
      await startPaymentFromOrderResponse(onlineOrder.data, selectedGateway, {
        autodebit: autoDebit,
      });
      setLoading(false);
    };

    pay();
  }, [onlineOrder.data, selectedGateway, autoDebit]);

  const handleGatewaySelect = async (gateway, installment = selectedInstallmentForPay) => {
    setOpenGatewayDialog(false);
    if (!gateway) return;

    const normalized = normalizeGateway(gateway);
    setSelectedGateway(normalized);
    setLoading(true);

    try {
      const payload = {
        weight: isMoneySip
          ? 0
          : Number(Number(calculatedGrams).toFixed(decimalPlaces)),
        metalName: isMoneySip
          ? "Money"
          : sip?.bullion?.metalName || sip?.metalName || "",
        orderType: "Installment",

        final_amount: totalPayable || sip.installmentAmount,

        tax: isMoneySip ? 0 : Number((totalPayable - metalCost).toFixed(2)),
        noOfMonths: sip?.noOfMonths,

        Id: installment?._id || sip?._id || "",
        sipDay: sip?.sipDay || 0,

        buyType: "Amount",
        purity: isMoneySip ? 0 : sip?.bullion?.purity || sip?.purity,

        transactionMode: normalized,
        platform: getPaymentPlatform(),

        installment_amount: sip?.installmentAmount,
        planid: sip?.sipplan?._id || sip?.moneysipplan?._id || sip?._id || "",
        moneysip: isMoneySip,

        tag: tag || "",

        x_api_version: "2022-09-01",
        jeweller: process.env.REACT_APP_JEWELLER_ID,

        autodebit: Boolean(autoDebit),
        interval: "",
      };
      savePaymentMeta(
        {
          grams: Number(Number(calculatedGrams).toFixed(decimalPlaces)),
          metal: sip?.bullion || { metalName: sip?.metalName },
          amount: totalPayable,
          totalAmount: totalPayable,
          tax: Number((totalPayable - metalCost).toFixed(2)),
          selectedGateway: normalized,
          orderType: payload.orderType,
          buyType: payload.buyType,
          planid: payload.planid,
          moneysip: isMoneySip,
          autodebit: Boolean(autoDebit),
        },
        { replace: true },
      );
      dispatch(resetOnlineOrderState());
      dispatch(createOnlineOrderRequest(payload));
    } catch (err) {
      console.error("order create error", err);
      toast.error("Payment initialization failed.");
      setLoading(false);
      setSelectedInstallmentForPay(null);
    }
  };

  const handlePayOfflineForInstallment = (installment) => {
    const finalAmount = Number(totalPayable) || 0;

    const planid =
      sip?.sipplan?._id || sip?.moneysipplan?._id || sip?._id || "";

    navigate("/offlineSipPayment", {
      state: {
        ...state,
        offlineSIPOrderData: {
          metalName: isMoneySip
            ? "Money"
            : sip?.metalName || sip?.bullion?.metalName,
          karat: isMoneySip
            ? ""
            : sip?.bullion?.karat || sip?.bullion?.caret || "",
          purity: isMoneySip
            ? 0
            : Number(sip?.bullion?.purity || sip?.purity || 0),
          weight: isMoneySip ? 0 : Number(calculatedGrams) || 0,
          tax: isMoneySip
            ? 0
            : Number(
                (totalPayable - (metalCost || 0)).toFixed(decimalPlaces),
              ),
          final_amount: finalAmount,
          installment_amount: Number(sip?.installmentAmount) || 0,
          noOfMonths: Number(sip?.noOfMonths) || 0,
          bullion: sip?.bullion,
          planid,
          moneysipplan: sip,
          isInstallment: true,
          Id: installment?._id || sip?._id || "",
          sipDay: Number(sip?.sipDay || 0),
          orderType: "Installment",
        },
      },
    });
  };

  const InstallmentCard = ({ item, index = 0, isPaid = false }) => {
    const payableAmount = isPaid
      ? Number(item?.totalPaidAmount ?? 0)
      : totalPayable;
    const planType = String(sipPlan?.type || "").toUpperCase();
    const discountPercent =
      isPaid && index === 0 && planType === "FIRST_AND_SUBSEQUENT"
        ? Number(sipPlan?.percentage || 0)
        : percentage;
    const discountedPrice =
      totalPayable - (discountPercent * totalPayable) / 100;

    const paidOn = item?.updatedAt || item?.createdAt || item?.installmentDate;

    return (
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 3,
          mb: 2,
          background: isPaid
            ? themeColors.mediumContainer
            : themeColors.mediumContainer,
          color: isPaid ? themeColors.text : themeColors.text,
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Grid size={{ xs: 8 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Installment amount: {formatCurrency(sip?.installmentAmount ?? 0)}
            </Typography>
            <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
              {isPaid ? "Paid Amount" : "Payable Amount"}:{" "}
              {formatCurrency(payableAmount)}
            </Box>
          </Grid>
          <Grid size={{ xs: 4 }} sx={{ textAlign: "right" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {isPaid ? item?.paymentMode || "Offline" : "Unpaid"}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13, mt: 1 }}>
              {isPaid
                ? `Paid On: ${formatDateTime(paidOn)}`
                : `SIP Date: ${formatDateTime(item?.installmentDate)}`}
            </Typography>
          </Grid>
        </Grid>

        {!isPaid && (
          <Box sx={{ mt: 1 }}>
            {effectiveTaxType === "GST" &&
              customerState?.toLowerCase() === jewellerState?.toLowerCase() && (
                <>
                  <Typography variant="body2">
                    CGST: {formatCurrency(cgst)}
                  </Typography>
                  <Typography variant="body2">
                    SGST: {formatCurrency(sgst)}
                  </Typography>
                </>
              )}

            {effectiveTaxType === "GST" &&
              customerState?.toLowerCase() !== jewellerState?.toLowerCase() && (
                <Typography variant="body2">
                  IGST: {formatCurrency(igst)}
                </Typography>
              )}

            {effectiveTaxType === "VAT" && (
              <>
                <Typography variant="body2">
                  TAX: {formatCurrency(totalPayable - metalCost)}
                </Typography>
                <Typography variant="body2">
                  CESS: {formatCurrency(cess)}
                </Typography>
              </>
            )}
          </Box>
        )}
        {discountPercent > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              Discounted {isPaid ? "Paid " : ""}Price {discountPercent}% :{" "}
              {formatCurrency(discountedPrice)}
            </Typography>
          </Box>
        )}

        {!isPaid && !sipPlan?.autodebit && (
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            {appConfig?.sipPaymentModeEnabled?.online && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => handlePayOnline(item)}
                disabled={loading}
                sx={{
                  background: themeColors.primary,
                  color: themeColors.text,
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                  borderRadius: "25px",
                  textTransform: "none",
                  py: 1,
                }}
              >
                {loading ? "Processing..." : "Pay Online"}
              </Button>
            )}

            {appConfig?.sipPaymentModeEnabled?.offline && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => handlePayOfflineForInstallment(item)}
                sx={{
                  background: themeColors.primary,
                  color: themeColors.text,
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                  borderRadius: "25px",
                  textTransform: "none",
                  py: 1,
                }}
              >
                Pay Offline
              </Button>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const InstallmentMap = ({ totalMonths, paidCount }) => {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
        {Array.from({ length: totalMonths }, (_, idx) => {
          const installmentNumber = idx + 1;
          const isPaid = installmentNumber <= paidCount;
          const isCurrent = installmentNumber === paidCount + 1;

          return (
            <Box
              key={installmentNumber}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: "bold",
                color: isPaid ? "#fff" : "#333",
                backgroundColor: isPaid
                  ? "#4CAF50"
                  : isCurrent
                    ? "#FF9800"
                    : "#e0e0e0",
                border: "1px solid #ccc",
                border: "1px solid #ccc",
              }}
            >
              {installmentNumber}
            </Box>
          );
        })}
      </Box>
    );
  };

  const HeaderCard = () => {
    const metal = sip?.bullion || {};
    const metalNameDisplay = metal?.metalName || sip?.metalName || "Money";
    const karat = metal?.karat || metal?.caret || "";
    const purity = metal?.purity || "";
    const metalColor = (metalNameDisplay || "").toLowerCase().includes("gold")
      ? "linear-gradient(135deg, #FFD700, #DAA520)"
      : (metalNameDisplay || "").toLowerCase().includes("silver")
        ? "linear-gradient(135deg, #C0C0C0, #E0E0E0)"
        : "linear-gradient(135deg, #B0BEC5, #ECEFF1)";

    return (
      <Box
        sx={{
          p: 2,
          borderRadius: "15px",
          background: themeColors.mediumContainer,
          color: themeColors.text,
          border: `1px solid ${themeColors.scaffold}`,
          mb: 3,
        }}
      >
        <Box
          sx={{
            background: metalColor,
            borderRadius: "15px",
            textAlign: "center",
            p: 1,
            mb: 1,
            mr: 3,
            width: "97%",
            border: `1px solid ${themeColors.scaffold}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#fff" }}>
            {isMoneySip
              ? "Money"
              : `${metalNameDisplay} - ${karat || ""}K - ${purity || ""}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "20px" }}>
            {sip?.sipplan?.name || sip?.name || "SIP Plan"}
          </Typography>
          <Typography sx={{ opacity: 0.8, fontSize: "14px" }}>
            {sipType} | {formatCurrency(sip?.installmentAmount)} /
            {sip?.sipplan?.durationType}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            Created On: {formatDateTime(sip?.createdAt)}
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            Ends On: {calculateEndDate(sip?.startDate, sip?.noOfMonths)}
          </Typography>
        </Box>

        <Typography sx={{ opacity: 0.8, fontSize: "12px", mt: 1 }}>
          Installment Map
        </Typography>
        <InstallmentMap
          totalMonths={sip?.noOfMonths || 0}
          paidCount={paidCount}
        />

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            Accumulated {metalNameDisplay} - {karat}k - {purity}
          </Typography>

          <Typography sx={{ fontSize: "14px" }}>
            Weight: {formatWeight(calculatedGrams)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        py: 3,
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          width: { xs: "95%", md: "65%", lg: "50vw" },
          borderRadius: "20px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
          background: themeColors.scaffoldGradientColors,
          color: themeColors.text,
          border: `1px solid ${themeColors.scaffold}`,
          p: 2,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              color: themeColors.primary,
              mb: 2,
            }}
          >
            Installments
          </Typography>

          {/* Header card */}
          <HeaderCard />

          {/* Tag input */}
          <Box sx={{ mt: 1 }}>
            <Typography
              sx={{ fontWeight: "bold", mb: 1, color: themeColors.text }}
            >
              Tag Your Plan (Optional)
            </Typography>
            <TextField
              fullWidth
              placeholder="Eg: Wedding SIP, Future Plan"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              sx={{
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                },
                "& .MuiInputLabel-root": {
                  color: themeColors.text,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.scaffold,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
              }}
            />
          </Box>

          {/* Terms */}
          {sip?.discalimer && sip.discalimer.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 1,
                borderRadius: 1,
                background: (theme) => theme.palette.grey[100],
              }}
            >
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                Terms & Conditions
              </Typography>
              <Typography sx={{ fontSize: 13, mb: 1 }}>
                <ReadMore
                  charLimit={240}
                  readMoreText={"...Show more"}
                  readLessText={" show less"}
                >
                  • {sip.discalimer}
                </ReadMore>
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>I agree</Typography>}
              />
            </Box>
          )}

          {/* Auto-debit */}
          {sipPlan?.autodebit && (
            <Box
              sx={{
                mt: 2,
                p: 1,
                borderRadius: 1,
                background: (theme) => theme.palette.primary.light,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoDebit}
                    onChange={(e) => setAutoDebit(e.target.checked)}
                    sx={{ color: "white" }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: "bold", color: "white" }}>
                      Auto Debit
                    </Typography>
                    <Typography sx={{ color: "white", fontSize: 13 }}>
                      By opting for Auto Debit, your installments will be auto
                      collected on the selected day.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}

          {percentage > 0 && (
            <Typography sx={{ mt: 1, fontSize: 14 }}>
              Discounted Price {percentage}% ={" "}
              {formatCurrency(
                Number(
                  (totalPayable - (percentage * totalPayable) / 100).toFixed(2),
                ),
              )}{" "}
              <Typography
                component="span"
                sx={{ color: "text.secondary", ml: 1 }}
              >
                (Original: {formatCurrency(totalPayable)})
              </Typography>
            </Typography>
          )}

          <Box sx={{ mt: 3 }}>
            <Accordion
              defaultExpanded
              sx={{
                background: themeColors.bigContainer,
                color: themeColors.text,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: themeColors.text }} />}
                aria-controls="paid-content"
                id="paid-header"
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 700 }}>Total Paid</Typography>
                  <Typography
                    sx={{ fontSize: 13 }}
                  >{`Total paid Installments : ${paidInstallments.length}`}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {paidInstallments.length === 0 ? (
                  <Typography>No paid installments found.</Typography>
                ) : (
                  paidInstallments.map((it, idx) => (
                    <InstallmentCard
                      key={it._id || idx}
                      item={it}
                      index={idx}
                      isPaid={true}
                    />
                  ))
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              defaultExpanded
              sx={{
                mt: 2,
                background: themeColors.bigContainer,
                color: themeColors.text,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: themeColors.text }} />}
                aria-controls="unpaid-content"
                id="unpaid-header"
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 700 }}>Unpaid</Typography>
                  <Typography
                    sx={{ fontSize: 13 }}
                  >{`Total unpaid Installments : ${Math.max(
                    0,
                    (sip?.noOfMonths || 0) - paidInstallments.length,
                  )}`}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {unpaidInstallments.length === 0 ? (
                  <Typography>No unpaid installments found.</Typography>
                ) : (
                  unpaidInstallments.map((it, idx) => (
                    <InstallmentCard
                      key={it._id || idx}
                      item={it}
                      index={idx}
                      isPaid={false}
                    />
                  ))
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </CardContent>
      </Card>
      <GatewaySelectionDialog
        open={openGatewayDialog}
        onClose={handleGatewaySelect}
        gateways={paymentGateways}
      />
    </Box>
  );
};

export default InstallmentScreen;
